import React, { useCallback } from "react";
import clsx from "clsx";
import Text from "../../../../../../shared-components/css-modules/lexus/atoms/Text";
import Alert from "../../../../../../shared-components/css-modules/lexus/molecules/Alert";
import baseStyles from "../../../common/modals/availableInOtherGrades/OtherGradeModal.module.scss";
import cardStyles from "../../../../../../shared-components/css-modules/lexus/molecules/Card.module.scss";
import engineStyles from "./Engine.module.scss";
import Icon from "../../../../../../shared-components/css-modules/lexus/atoms/Icon";
import HybridLabel from "../../../../../../shared-components/css-modules/lexus/molecules/HybridLabel";
import Button from "../../../../../../shared-components/css-modules/lexus/atoms/Button";
import { getEcoTagType } from "../../../../../../../shared-logic/utils/typedUtils";
import { useCarConfigSelector } from "../../../../../redux/store";
import {
    getEcoBadgeType,
    getWLTPLabel,
    hasAlternativeEcoSpecFlow,
} from "../../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { trackEngineTooltip } from "../../../../../utils/tracking";
import Popover from "../../../../../../shared-components/css-modules/lexus/vendor/popover/Popover";
import a11y from "../../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import useAphAvailableInOtherGrades from "../../../../../hooks/useAphAvailableInOtherGrades";
import GradeCard from "../../../common/modals/availableInOtherGrades/GradeCard";
import type { AvailableGradesForConfigurableGradeType } from "../../../../../../../shared-logic/types/AvailableInOtherGradesType";
import EcoLabel from "../../../../../../shared-components/css-modules/lexus/molecules/EcoLabel";
import EfficiencyClass from "../../../common/eco/EfficiencyClass";
import { getEcoBadgeUrl } from "../../../../../../../../common-deprecated/utils";
import { filterEcoSpecs } from "../../../../../../../../common-deprecated/utils/specs";
import { getMotorizationLabelConfig } from "../../../../../utils/motorizationUtils";
import type { MotorizationSpecType, MotorizationType } from "../../../../../../../shared-logic/types/Motorization";
import { ecoTagToLabel } from "../../../../../../../../common-deprecated/utils/ecoTagUtils";
import { CarConfigLabel, getCarConfigLabel } from "../../../../../utils/labelUtils";
import { calculateUnitPosition } from "../../../../../../shared-logic/utils/rtlUitils";

const EngineOtherGradesModal: React.FC<{
    motorization: MotorizationType;
}> = ({ motorization }) => {
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const { grade, model } = useCarConfigSelector((state) => state.car);

    const { isActive, availableGradesForConfigurable, selectedGrade, setSelectedGrade } =
        useAphAvailableInOtherGrades();

    const alternativeEcoSpecFlow = hasAlternativeEcoSpecFlow(commonSettings);
    const ecoTag = getEcoTagType({
        id: motorization.engine.id,
        name: motorization.engine.name,
        hybrid: motorization.engine.type.hybrid,
        electric: motorization.engine.type.electric,
        fuelCell: motorization.engine.type.fuelCell,
        plugin: motorization.engine.type.plugin,
        internalCode: motorization.engine.internalCode,
        type: motorization.engine.type.name,
        typeId: motorization.engine.type.id,
        typeCode: motorization.engine.type.internalCode,
    });
    const ecoTagLabel = ecoTagToLabel(ecoTag);
    const ecoBadge = getEcoBadgeType(commonSettings, ecoTag, model.id);
    const ecoBadgeUrl = getEcoBadgeUrl(ecoBadge, commonSettings);
    const efficiencyLabelConfig = getMotorizationLabelConfig(motorization, alternativeEcoSpecFlow);

    const wltpLabel = getWLTPLabel(
        commonSettings,
        motorization.engine.certification,
        motorization.engine.type.electric,
    );

    const onWltpClick = useCallback((isOpen: boolean) => {
        if (isOpen) {
            trackEngineTooltip("wltp");
        }
    }, []);

    const onClickHandler = useCallback(
        (availableGrade: AvailableGradesForConfigurableGradeType) => {
            if (!isActive(availableGrade.id)) setSelectedGrade(availableGrade);
        },
        [isActive, setSelectedGrade],
    );

    const specs = alternativeEcoSpecFlow
        ? motorization.specs
        : filterEcoSpecs<MotorizationSpecType>(motorization.specs);

    if (!availableGradesForConfigurable) return null;

    return (
        <>
            <div className={baseStyles.intro}>
                <Text tag="h2" variant="apheleia-h4">
                    {motorization.wheeldrive.name}
                </Text>
                {/* // TODO: set variant Lexus */}
                <Text tag="p" variant="apheleia-body-large">
                    {motorization.wheeldrive.description}
                </Text>
                <div className={clsx(cardStyles.card, baseStyles["engine-card"])}>
                    {/*TODO you can copy the specs from the EngineItem.tsx file, labels are not added in design, to ask if needed */}
                    <div className={engineStyles["engine-header-labels"]}>
                        {ecoBadgeUrl && <EcoLabel url={ecoBadgeUrl} />}
                        {efficiencyLabelConfig.show && <EfficiencyClass label={efficiencyLabelConfig.label} />}
                        {ecoTagLabel && (
                            <HybridLabel
                                label={getCarConfigLabel({ commonSettings, carConfigSettings }, ecoTagLabel)}
                                hideBackground
                                variant={ecoTag}
                            />
                        )}
                    </div>
                    <Text variant="apheleia-body-small" spacing="spacing-0" className={engineStyles.motorization}>
                        {motorization.engine.name}, {motorization.transmission.name}
                    </Text>
                    {specs.length !== 0 && (
                        <div className={clsx(engineStyles.specs, baseStyles["engine-specs"])}>
                            {specs.map((spec) => (
                                <Text variant="apheleia-body" color="grey-7" key={spec.id}>
                                    {spec.name}
                                    {wltpLabel && (
                                        <Popover placement="top" onOpenChangeListen={onWltpClick}>
                                            <Popover.Trigger>
                                                <Button
                                                    variant="clean"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                    }}
                                                >
                                                    <Icon variant="info" sizing="md" aria-hidden="true" />
                                                    <span className={a11y["sr-only"]}>
                                                        <CarConfigLabel label="carConfigA11yButtonToggleFuelInfo" />
                                                    </span>
                                                </Button>
                                            </Popover.Trigger>
                                            <Popover.Content>
                                                <div dangerouslySetInnerHTML={{ __html: wltpLabel }} />
                                            </Popover.Content>
                                        </Popover>
                                    )}
                                    <br />
                                    <strong>
                                        <Text variant="apheleia-body">
                                            {calculateUnitPosition(commonSettings.culture.name, spec.value, spec.unit)}
                                        </Text>
                                    </strong>
                                </Text>
                            ))}
                        </div>
                    )}
                </div>
                <Text tag="p" variant="apheleia-body-large">
                    <CarConfigLabel
                        label={
                            availableGradesForConfigurable.grades.length > 1
                                ? "carConfigSelectAvailableEngineInOtherGrades"
                                : "carConfigSelectAvailableEngineInOtherGrade"
                        }
                        params={{
                            model: model.webtitleSectionName,
                            currentGrade: grade.webtitleSectionName,
                            newGrade: selectedGrade?.name,
                        }}
                    />
                </Text>
            </div>

            <Alert icon="info">
                <CarConfigLabel label="carConfigSelectAvailableItemInOtherGradeWarning" />
            </Alert>
            <div className={baseStyles.gradelist}>
                {selectedGrade &&
                    availableGradesForConfigurable?.grades.map((availableGrade) => (
                        <GradeCard
                            onClickHandler={onClickHandler}
                            key={availableGrade.id}
                            availableGrade={availableGrade}
                            isActive={isActive(availableGrade.id)}
                        />
                    ))}
            </div>
        </>
    );
};

export default EngineOtherGradesModal;
